import { useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { copyText } from './copyText';
import {
  GUIDE_PLATFORMS,
  TROUBLESHOOTING,
  detectPlatform,
  type GuideApp,
  type PlatformId,
} from './guideData';
import styles from './ConnectGuide.module.css';

interface ConnectGuideProps {
  /** Ссылка подписки; пустая строка — кнопки «Скопировать», «Открыть в приложении» и QR недоступны */
  subscriptionUrl: string;
  /** Вкладка устройства при открытии; по умолчанию — по user-agent */
  initialPlatform?: PlatformId;
  /** Ключ уже в буфере обмена — кнопку «Скопировать» в шаге 2 не показываем */
  copied?: boolean;
}

/** Смена региона App Store — для iPhone, если Happ не находится в российском магазине */
const REGION_STEPS: ReactNode[] = [
  <>
    Откройте <strong>«Настройки»</strong> на iPhone и нажмите на своё имя вверху экрана.
  </>,
  <>
    Выберите <strong>«Медиаматериалы и покупки»</strong>, затем{' '}
    <strong>«Просмотреть учётную запись»</strong>. Система может попросить войти.
  </>,
  <>
    Нажмите <strong>«Страна/регион»</strong>, затем <strong>«Изменить страну или регион»</strong>.
  </>,
  <>
    Выберите в списке <strong>«Казахстан»</strong> и примите условия.
  </>,
  <>
    Способ оплаты — <strong>«Нет»</strong> (None). Адрес укажите любой казахстанский: например,
    город Алматы, индекс 050000. Телефон — любой в формате +7 7XX XXX XX XX.
  </>,
  <>
    Нажмите <strong>«Готово»</strong>. Регион изменён — App Store станет казахстанским.
  </>,
  <>
    Найдите в App Store приложение <strong>Happ</strong> и установите его.
  </>,
];

const PLATFORM_ICONS: Record<PlatformId, ReactElement> = {
  android: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12" y2="18.01" />
    </svg>
  ),
  ios: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06z" /><path d="M10 2c1 .5 2 2 2 5" />
    </svg>
  ),
  pc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  tv: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="15" rx="2" /><polyline points="17 2 12 7 7 2" />
    </svg>
  ),
};

/**
 * Тело инструкции «Подключиться»: вкладки устройств, плитки приложений, три шага
 * и «Что-то не работает?». Используется на странице /dashboard/guide и в ConnectModal.
 */
export function ConnectGuide({ subscriptionUrl, initialPlatform, copied = false }: ConnectGuideProps) {
  const [platformId, setPlatformId] = useState<PlatformId>(
    () => initialPlatform ?? detectPlatform(typeof navigator === 'undefined' ? '' : navigator.userAgent),
  );
  const [appId, setAppId] = useState<string>('happ');
  const [showRegionSteps, setShowRegionSteps] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const platform = GUIDE_PLATFORMS.find((p) => p.id === platformId) ?? GUIDE_PLATFORMS[0];
  const app: GuideApp = platform.apps.find((a) => a.id === appId) ?? platform.apps[0];

  const selectPlatform = (id: PlatformId) => {
    setPlatformId(id);
    setAppId('happ');
    setShowQr(false);
  };

  const handleCopy = async () => {
    if (!subscriptionUrl) return;
    const ok = await copyText(subscriptionUrl);
    if (ok) toast.success('Ссылка скопирована');
    else toast.error('Не удалось скопировать, выделите ссылку вручную');
  };

  const deepLink = app.deepLink && subscriptionUrl ? app.deepLink(subscriptionUrl) : null;

  return (
    <div className={styles.root}>
      {/* Платформы */}
      <div className={styles.tabs} role="tablist" aria-label="Устройство">
        {GUIDE_PLATFORMS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={p.id === platform.id}
            className={`${styles.tab} ${p.id === platform.id ? styles.tabActive : ''}`}
            onClick={() => selectPlatform(p.id)}
          >
            <span className={styles.tabIcon}>{PLATFORM_ICONS[p.id]}</span>
            {p.label}
          </button>
        ))}
      </div>

      {platform.id === 'ios' && (
        <section className={styles.region}>
          <button
            type="button"
            className={`${styles.regionCard} ${showRegionSteps ? styles.regionCardActive : ''}`}
            onClick={() => setShowRegionSteps((v) => !v)}
            aria-expanded={showRegionSteps}
          >
            <span className={styles.regionTitle}>Сменить регион App Store на Казахстан</span>
            <span className={styles.regionDesc}>
              VPN-клиенты удалены из российского App Store. Сначала смените регион, потом ставьте
              приложение. {showRegionSteps ? 'Свернуть шаги' : 'Показать шаги'}
            </span>
          </button>
          {showRegionSteps && (
            <>
              <div className={styles.important}>
                <div className={styles.importantTitle}>Важно</div>
                Регион можно вернуть обратно в любой момент тем же путём.
              </div>
              <ol className={styles.regionSteps}>
                {REGION_STEPS.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </>
          )}
        </section>
      )}

      {/* Приложения */}
      <div className={styles.sectionLabel}>Выберите приложение</div>
      <div className={styles.apps}>
        {platform.apps.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`${styles.appCard} ${a.id === app.id ? styles.appCardActive : ''}`}
            onClick={() => setAppId(a.id)}
            aria-pressed={a.id === app.id}
          >
            <span className={styles.appLetter} aria-hidden="true">
              {a.name[0]}
            </span>
            <span className={styles.appText}>
              <span className={styles.appName}>{a.name}</span>
              {a.tagline && <span className={styles.appTagline}>{a.tagline}</span>}
            </span>
            {a.recommended && <span className={styles.appStar} title="Рекомендуем">★</span>}
          </button>
        ))}
      </div>

      {/* Шаги */}
      <ol className={styles.steps}>
        <li className={styles.step}>
          <span className={styles.stepNum}>1</span>
          <div className={styles.stepBody}>
            <div className={styles.stepTitle}>Скачайте {app.name}</div>
            <div className={styles.stepText}>{platform.downloadFor}</div>
            <div className={styles.btnRow}>
              {app.downloads.map((d) => (
                <a
                  key={d.url}
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {app.downloads.length === 1 ? 'Скачать' : d.label}
                </a>
              ))}
            </div>
          </div>
        </li>

        <li className={styles.step}>
          <span className={styles.stepNum}>2</span>
          <div className={styles.stepBody}>
            {subscriptionUrl ? (
              <>
                <div className={styles.stepTitle}>Добавьте подписку</div>
                <div className={styles.stepText}>
                  {deepLink
                    ? 'Нажмите «Открыть в приложении» — ключ добавится сам.'
                    : copied
                      ? 'Ключ уже скопирован — вставьте его в приложении.'
                      : 'Скопируйте ключ и вставьте его в приложении.'}
                </div>
                <div className={styles.btnRow}>
                  {deepLink && (
                    <a className={`${styles.btn} ${styles.btnPrimary}`} href={deepLink}>
                      Открыть в приложении
                    </a>
                  )}
                  {!copied && (
                    <button
                      type="button"
                      className={`${styles.btn} ${deepLink ? styles.btnSecondary : styles.btnPrimary}`}
                      onClick={handleCopy}
                    >
                      Скопировать
                    </button>
                  )}
                  {platform.id !== 'tv' && (
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnGhost}`}
                      onClick={() => setShowQr((v) => !v)}
                      aria-expanded={showQr}
                    >
                      {showQr ? 'Скрыть QR' : 'QR-код'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.stepTitle}>Добавьте ключ</div>
                <div className={styles.stepText}>
                  Выберите нужный вам ключ на главной, скопируйте его и вставьте в приложение.
                </div>
                <div className={styles.btnRow}>
                  <Link className={`${styles.btn} ${styles.btnPrimary}`} to="/dashboard">
                    Выбрать ключ на главной
                  </Link>
                </div>
                <div className={styles.hint}>{app.addHint}</div>
              </>
            )}
            {subscriptionUrl && <div className={styles.hint}>{app.addHint}</div>}
            {(showQr || platform.id === 'tv') && subscriptionUrl && (
              <div className={styles.qr}>
                <QRCodeSVG value={subscriptionUrl} size={168} level="M" bgColor="transparent" fgColor="var(--text)" />
                <span>Отсканируйте в приложении на другом устройстве</span>
              </div>
            )}
          </div>
        </li>

        <li className={styles.step}>
          <span className={styles.stepNum}>3</span>
          <div className={styles.stepBody}>
            <div className={styles.stepTitle}>Включите VPN</div>
            <div className={styles.stepText}>
              Выберите любой сервер из списка и нажмите кнопку подключения.
              {platform.id === 'ios' && ' При первом запуске iOS спросит разрешение на VPN-конфигурацию — разрешите.'}
            </div>
            {app.note && <div className={styles.note}>{app.note}</div>}
          </div>
        </li>
      </ol>

      <details className={styles.trouble}>
        <summary>Что-то не работает?</summary>
        <ul>
          {TROUBLESHOOTING.map((t) => (
            <li key={t.title}>
              <strong>{t.title}.</strong> {t.text}
            </li>
          ))}
          <li>
            <strong>Остались вопросы.</strong> Напишите в <Link to="/dashboard/support">поддержку</Link>,
            укажите приложение и устройство.
          </li>
        </ul>
      </details>
    </div>
  );
}
